import { newRunOptions } from '../flow';
import { render } from 'preact';
import type Phaser from 'phaser';
import { Bar } from './Bar';
import { Currencies } from './Currencies';
import { SpellBar } from './SpellBar';
import { hudState } from './state';
import { Hub, hubState } from './hub';
import { Doors } from './panels/Doors';
import { LevelUp } from './panels/LevelUp';
import { Shop } from './panels/Shop';
import { EndPanel } from './panels/EndPanel';
import { StatsPanel, useStatsHotkey } from './panels/StatsPanel';
import { MuteButton } from '../audio/AudioControls';

function Hud({ game }: { game: Phaser.Game }) {
  const s = hudState;
  const inRun = hubState.booted.value && !hubState.visible.value;
  const phase = s.phase.value;
  const boss = s.boss.value;
  const shop = s.shop.value;
  useStatsHotkey(inRun);
  // comme un lancement depuis le hub : les succès annoncés à la fin de la run précédente ne le sont qu'une fois
  const replay = () => { hubState.newAchievements.value = []; game.scene.stop('Room'); game.scene.start('Room', newRunOptions()); };
  const backToHub = () => { game.scene.stop('Room'); game.scene.start('Hub'); hubState.visible.value = true; hudState.reset(); };
  return (
    <div class="hud">
      <Hub game={game} />
      {inRun && (
        <>
          <div class="hud-top">
            <Bar value={s.hp.value} max={s.hpMax.value} color="var(--rog-red)" label={`PV ${Math.ceil(s.hp.value)} / ${s.hpMax.value}`} />
            <Bar value={s.xp.value} max={s.xpNext.value} color="var(--rog-gold)" label={`Niv. ${s.level.value}`} />
            <div class="hud-room">Salle {s.roomIndex.value + 1} / {s.roomsTotal.value}</div>
            <div class="hud-class">{s.className.value}</div>
            <div class="hud-wave">Vague {Math.min(s.wave.value, s.waves.value)} / {s.waves.value}</div>
            {s.modifiers.value.length > 0 && <div class="hud-modifiers">Modificateurs : {s.modifiers.value.join(', ')}</div>}
          </div>
          <Currencies kamas={s.kamas.value} reflets={hubState.meta.value.reflets} ogrines={hubState.meta.value.ogrines} />
          <div class="panel hud-stats-toggle-wrap">
            <button class="hud-stats-toggle" onMouseDown={(e) => e.preventDefault()} aria-pressed={s.statsOpen.value} aria-label="Caractéristiques (C)" onClick={() => { s.statsOpen.value = !s.statsOpen.value; }}>Stats<span class="hud-key"> (C)</span></button>
          </div>
          {s.autohit.value && <div class={`hud-autohit hud-autohit-${s.autohit.value}`} role="status" aria-label={`Autohit ${s.autohit.value === 'on' ? 'actif' : 'en attente'}`}><span class="hud-autohit-dot" aria-hidden="true" />Autohit</div>}
          {s.statsOpen.value && phase === 'combat' && <StatsPanel />}
          {boss && (
            <div class="boss-bar" aria-label="Boss">
              <span class="boss-name">{boss.name}</span>
              <Bar value={boss.hp} max={boss.hpMax} color="var(--rog-red)" label={`${Math.ceil(boss.hp)} / ${boss.hpMax}`} />
            </div>
          )}
          <SpellBar spells={s.spells.value} />
          <div class="panel hud-mute-wrap"><MuteButton /></div>
          {phase === 'doors' && <Doors doors={s.doors.value} />}
          {phase === 'levelUp' && <LevelUp cards={s.cards.value} effects={s.cardEffects.value} rerolls={s.rerolls.value} />}
          {phase === 'shop' && shop && <Shop shop={shop} kamas={s.kamas.value} equipment={s.equipment.value} />}
        </>
      )}
      {s.ended.value && <EndPanel summary={s.ended.value} onReplay={replay} onHub={backToHub} />}
      {/* après l'écran de fin : les toasts (succès gagnés à la fin de la run) restent visibles par-dessus */}
      {(inRun || s.ended.value) && <div class="hud-toasts">{s.toasts.value.map((t) => <div key={t.id} class={t.kind === 'achievement' ? 'toast achievement' : 'toast'}>{t.text}</div>)}</div>}
    </div>
  );
}

export function mountHud(root: HTMLElement, game: Phaser.Game): void { render(<Hud game={game} />, root); }
